import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
// [A11Y]: Accessibility / dyslexia-friendly overlay. Loads AFTER index.css (and therefore
// after Tailwind's generated utilities) so equal-specificity author rules in themes.css
// win without needing !important on every declaration. Inert until AppearanceController
// sets data-a11y on <html> — zero visual change when accessibility settings are all off.
import './themes.css';
import TabletopInventoryManager from './App';
import CrashScreen from './components/CrashScreen';
import { APP_VERSION } from './constants/version';
import { validateAndSanitizeStorage, emergencyClearStorage } from './utils/storageValidator';
import { installChunkLoadErrorHandler } from './utils/chunkLoadErrorHandler';
import { unregisterAllServiceWorkers } from './utils/unregisterServiceWorker';

// [S6]: Initialize Sentry error tracking before anything else renders.
// DSN is set via REACT_APP_SENTRY_DSN env var (Vercel + local .env.local).
// If DSN is missing, Sentry silently no-ops — safe for local dev without config.
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    release: `tti@${APP_VERSION}`,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // [WHY]: 10% trace sampling is plenty for launch-scale traffic.
    // Bump higher if traffic is low and you need more performance data.
    tracesSampleRate: 0.1,
    // [WHY]: Session replay off by default for user privacy.
    // On-error replay captures the session leading up to a crash.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      // [PURPOSE]: Scrub any character data that might leak into breadcrumbs or request body.
      // We never want PII or custom content text in Sentry.
      if (event.request?.data) delete event.request.data;
      return event;
    },
  });
}

// [S6]: Capture unhandled promise rejections explicitly.
// Sentry's GlobalHandlers integration catches these by default, but this provides
// defense in depth and a clear breadcrumb in console for debugging.
window.addEventListener('unhandledrejection', (event) => {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureException(event.reason || new Error('Unhandled promise rejection'));
  }
});

// [FIX - NOV 3 SESSION 6]: Validate localStorage before app starts
// This prevents "Cannot convert null to object" crashes from corrupt data

try {
  const validation = validateAndSanitizeStorage();

  if (!validation.valid) {
    console.warn('⚠️ Found corrupt localStorage data, cleaned up', validation.cleared);

    // If many keys were cleared, suggest full reset
    if (validation.cleared.length > 5) {
      console.error('🚨 Multiple corrupt keys found. Consider clearing ALL localStorage.');
    }
  }
} catch (error) {
  console.error('❌ localStorage validation failed catastrophically:', error);
  console.error('🚨 Performing emergency clear...');

  try {
    emergencyClearStorage();
    alert('Storage was corrupted and has been reset. The app will reload.');
    window.location.reload();
  } catch (clearError) {
    console.error('❌ Emergency clear also failed:', clearError);
    alert('Critical error: Unable to access localStorage. Please clear browser data manually.');
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {/* [S6]: Outermost error boundary — catches anything that escapes AppProviders' inner ErrorBoundary.
        CrashScreen provides user-friendly UI with error ID, reload, and support contact.
        Sentry.ErrorBoundary automatically reports the error to Sentry before rendering fallback. */}
    <Sentry.ErrorBoundary fallback={({ error, eventId, resetError }) => (
      <CrashScreen error={error} eventId={eventId} onReset={resetError} />
    )}>
      <TabletopInventoryManager />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// [BATCH 0.5 — 2026-05-03]: Service worker UNREGISTERED for v1.0.
// [WHY]: Original public/sw.js used cache-first with a static CACHE_NAME and cached
//        non-existent /static/js/bundle.js paths (CRA prod outputs main.HASH.js).
//        Result: returning users got served a stale index.html referencing chunk
//        hashes that no longer existed on the new deploy → ChunkLoadError on sign-in.
//        See docs/plans/AUDIT_2026-05-02_PHASE3_release_completion_plan.md, Batch 0.5.
// [INTEGRATION]: A proper PWA / offline-mode SW will return in v1.0.1 / v1.1 (network-
//        first HTML, cache-first /static/* with deploy-time CACHE_NAME bump).
if (!window.Capacitor) {
  // Fire-and-forget: clear any pre-existing SW + caches so returning users
  // are not stuck on the broken legacy SW. Idempotent and safe to repeat.
  unregisterAllServiceWorkers({ navigator: window.navigator, caches: window.caches })
    .catch(() => { /* best-effort cleanup; nothing actionable on failure */ });
}

// [BATCH 0.5 — 2026-05-03]: Detect post-deploy ChunkLoadError and reload once.
// [WHY]: Even with the SW gone, browsers cache index.html briefly. A user mid-session
//        when a deploy lands can still see "Loading chunk N failed." A single
//        page reload pulls a fresh index.html with current chunk references.
//        Loop guard (5 min) prevents infinite reload on a genuinely-bad deploy.
installChunkLoadErrorHandler({
  target: window,
  reload: () => window.location.reload(),
  storage: window.sessionStorage,
});